import { Button } from '@/components/ui/button';
import React, { useState } from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Popover } from '@/components/Popover';
import { cn } from '@/utils/classname';
import { copyToClipboard } from '@/utils/copyToClipboard.util';

interface AgentIPAddressesDropdownProps {
  ips: string[];
}

/**
 * Component for displaying agent IP addresses with Primary IP + Badge pattern
 *
 * Displays:
 * - Primary IP (first IP) inline for immediate visibility
 * - Badge showing count of additional IPs (if any)
 * - Popover with additional IPs when badge is clicked
 * - Copy functionality for individual IPs and all IPs
 */
export const AgentIPAddressesDropdown: React.FC<
  AgentIPAddressesDropdownProps
> = ({ ips }) => {
  const [open, setOpen] = useState(false);

  if (ips.length === 0) return null;

  const primaryIp = ips[0];
  const additionalIps = ips.slice(1);
  const hasAdditional = additionalIps.length > 0;
  const additionalCount = additionalIps.length;

  // Singular/plural for accessibility label
  const ipLabel = additionalCount === 1 ? 'IP address' : 'IP addresses';

  return (
    <div className="flex items-center gap-2">
      {/* Primary IP inline */}
      <CopyToClipboard
        className="text-chariot-text-secondary text-sm"
        iconClassName="size-3"
      >
        {primaryIp}
      </CopyToClipboard>

      {/* Badge for additional IPs */}
      {hasAdditional && (
        <Popover
          open={open}
          setOpen={setOpen}
          className="overflow-auto p-0"
          placement="bottom-start"
          allowFlip={false}
          container={
            <button
              className={cn(
                'bg-chariot-background-secondary text-chariot-text-tertiary',
                'hover:bg-chariot-background-tertiary',
                'rounded-md px-1.5 py-0.5 text-xs',
                'transition-colors'
              )}
              onClick={() => setOpen(!open)}
              aria-label={`Show ${additionalCount} additional ${ipLabel}`}
              aria-expanded={open}
              aria-haspopup="listbox"
            >
              +{additionalCount}
            </button>
          }
        >
          <div
            className="flex flex-col gap-1 p-4 pb-3"
            role="listbox"
            aria-label="Additional IP addresses"
          >
            {additionalIps.map((ip, index) => (
              <div key={ip} role="option" aria-selected={false}>
                <CopyToClipboard
                  className="flex w-full justify-between"
                  iconClassName="size-3"
                  alwaysShowCopyIcon={true}
                >
                  <div className="mr-4 text-sm">{ip}</div>
                </CopyToClipboard>
                {index !== additionalIps.length - 1 && (
                  <div className="border-chariot-stroke my-1 h-px border-t" />
                )}
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-fit rounded-full py-0"
              onClick={() => {
                copyToClipboard(ips.join(', '));
              }}
            >Copy All IPs</Button>
          </div>
        </Popover>
      )}
    </div>
  );
};
